import React from "react";
import { useQuery } from "@tanstack/react-query";
import Checkbox from "../../atoms/checkbox";
import { useJoinTermsList } from "../../../@apis/hooks/useAuth";
import { TERMS_LABEL } from "../../../lib/constants/common.constants";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import type { SignUpInput } from "../../../types/auth.types";
import { useModalStore } from "../../../lib/store/useModalStore";
import { useTermsAgreeStore } from "../../../lib/store/useTermsAgreeStore";
import { cn } from "../../../lib/utils/cn";

export default function TermsScreen() {
  const { openModal } = useModalStore();
  const { data: termsData, isSuccess } = useQuery({ ...useJoinTermsList() });
  const { success, data } = termsData || {};

  const { control, setValue } = useFormContext<SignUpInput>();

  // 약관 모달 "동의" 신호 반영 (모달은 전역 렌더라 폼 컨텍스트 밖)
  const pendingAgreeId = useTermsAgreeStore((s) => s.pendingAgreeId);
  const clearPendingAgree = useTermsAgreeStore((s) => s.clear);
  React.useEffect(() => {
    if (pendingAgreeId == null) return;
    const index = (data?.termsList ?? []).findIndex(
      (term) => term.id === pendingAgreeId,
    );
    if (index >= 0) {
      setValue(
        `joinTermsAgreeList.${index}`,
        { termsId: pendingAgreeId, isAgreed: true },
        { shouldDirty: true, shouldValidate: true },
      );
    }
    clearPendingAgree();
  }, [pendingAgreeId, data, setValue, clearPendingAgree]);

  const agreeList = useWatch({ control, name: "joinTermsAgreeList" });
  const termsCount = data?.termsList?.length ?? 0;
  const allChecked =
    termsCount > 0 &&
    (agreeList?.length ?? 0) === termsCount &&
    (agreeList ?? []).every((item) => item?.isAgreed);

  const toggleAll = () => {
    const next = !allChecked;
    (data?.termsList || []).forEach(({ id }, index) => {
      setValue(
        `joinTermsAgreeList.${index}`,
        { termsId: id, isAgreed: next },
        { shouldDirty: true, shouldValidate: true },
      );
    });
  };

  return (
    <div>
      <div className="space-y-[12px]">
        <h1 className="font-semibold">약관에 동의해 주세요.</h1>
        <p className="text-xs text-[#8e8e93]">
          서비스 이용을 위해 아래 약관 동의가 필요해요.
        </p>
      </div>

      <div
        className="flex-row-between pb-[16px] border-b border-[#e6e6e6] cursor-pointer select-none mt-[36px]"
        role="button"
        tabIndex={0}
        onClick={toggleAll}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleAll();
          }
        }}
      >
        <Checkbox
          className="size-4"
          label="전체 동의"
          checked={allChecked}
          readOnly
        />
      </div>

      <div className="mt-[16px] space-y-[12px]">
        {isSuccess && success && (
          <>
            {(data?.termsList || []).map(
              ({ id, type, term, isRequired }, index) => (
                <Controller
                  control={control}
                  key={id}
                  name={`joinTermsAgreeList.${index}`}
                  defaultValue={{ termsId: id, isAgreed: false }}
                  render={({ field }) => {
                    const toggleAgree = () => {
                      field.onChange({
                        ...field.value,
                        isAgreed: !field.value.isAgreed,
                      });
                    };
                    const openTermsModal = (e: React.MouseEvent) => {
                      e.stopPropagation();
                      openModal("TERMS_MODAL", {
                        html: term,
                        type,
                        // 이미 동의한 약관은 모달 하단 동의 버튼 미노출
                        agreeTermsId: field.value.isAgreed ? undefined : id,
                      });
                    };
                    return (
                      <div
                        className="flex-row-between cursor-pointer select-none items-center gap-x-2"
                        role="button"
                        tabIndex={0}
                        onClick={toggleAgree}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleAgree();
                          }
                        }}
                      >
                        <Checkbox
                          className="size-4 "
                          label={
                            <p>
                              <span
                                className={cn(
                                  isRequired
                                    ? "text-[#ff3b30]"
                                    : "text-[#8e8e93]",
                                )}
                              >
                                {isRequired ? "[필수] " : "[선택] "}
                              </span>
                              {TERMS_LABEL[type]} 동의
                            </p>
                          }
                          checked={field.value.isAgreed}
                        />

                        <div
                          className="flex items-center font-semibold text-[#8e8e93] text-xs gap-x-[6px] shrink-0"
                          onClick={openTermsModal}
                        >
                          보기
                          <img
                            className="w-[6px] h-[14px] cursor-pointer"
                            src="/icons/ic-go.webp"
                            role="button"
                          />
                        </div>
                      </div>
                    );
                  }}
                />
              ),
            )}
          </>
        )}
      </div>
    </div>
  );
}
